import { PHASE_PRODUCTION_BUILD } from 'next/constants'

import { Container } from 'src/components/Container/Container'
import { Divider } from 'src/components/Divider/Divider'
import { PostModule } from 'src/components/Post/PostModule'
import { ProductDetail } from 'src/components/ProductDetail/ProductDetail'
import { ProductDetailSEO } from 'src/components/ProductDetail/ProductDetailSEO'
import { ProductReviews } from 'src/components/ProductDetail/ProductReviews'
import { config } from 'src/config'
import { graphQLClient } from 'src/data/graphql/graphql-client'
import { ProductDocument, RecommendedProductsDocument } from 'src/data/graphql-generated'
import { fetchProductPost } from 'src/data/providers/fetchProductPost'
import { fetchProductReviews } from 'src/data/providers/fetchProductReviews'
import { fetchProductSlugs } from 'src/data/providers/fetchProductSlugs'
import { getAlternateLang } from 'src/functions/i18n/getAlternateLang'
import { getLanguageFromLocale, getLocaleFromLanguage } from 'src/functions/i18n/localeFns'
import { getProductAlternateHandle } from 'src/functions/product/getProductAlternateHandle'
import { getProductBreadcrumbs } from 'src/functions/product/getProductBreadcrumbs'
import { getMetafield } from 'src/functions/util/getMetafield'

import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Product, ProductQuery, RecommendedProductsQuery } from 'src/data/graphql-generated'
import type { Language } from 'src/enum/Language'
import type { Locale } from 'src/enum/Locale'
import type { ShopCanonicals } from 'src/types/Canonical'
import type { Post } from 'src/types/Post'
import type { PaginatedProductReviews } from 'src/types/Product'

interface ProductDetailPageProps {
  product: Product
  post: Post
  reviews: PaginatedProductReviews
  recommendedProducts: Product[]
  handles: ShopCanonicals
}

export const ProductDetailPage = ({ product, post, reviews, recommendedProducts, handles }: ProductDetailPageProps) => {
  const {
    collections: { nodes: productCollections },
    productType,
    metafields,
  } = product

  const breadcrumbs = getProductBreadcrumbs(productCollections, metafields, productType)

  return (
    <>
      <ProductDetailSEO product={product} handles={handles} />

      <ProductDetail
        product={product}
        rating={reviews.rating!}
        reviews={reviews.data}
        recommendedProducts={recommendedProducts}
        breadcrumbs={breadcrumbs}
      />

      {post?.modules_and_components.map((module, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <PostModule key={index} module={module} isProductModule />
      ))}
      <Divider />

      {reviews.data.length > 0 && (
        <Container id="reviews">
          <ProductReviews
            product={product}
            rating={reviews.rating!}
            initialReviews={reviews.data}
            initialReviewsMeta={reviews.meta}
          />
        </Container>
      )}

      <Divider />
    </>
  )
}

const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params) {
    return { props: {} }
  }

  let { slug, lang } = params
  slug = slug as string
  lang = lang as string

  const { product }: ProductQuery = await graphQLClient.request({
    document: ProductDocument,
    variables: { handle: slug, lang: lang.toUpperCase() },
  })

  if (!product?.variants?.nodes?.length) {
    return { props: {} }
  }

  const {
    variants: { nodes: productVariants },
    id,
  } = product

  const productSkus = productVariants.map((variant) => variant.sku)
  const alternateLang = getAlternateLang(lang as Language)

  const { productRecommendations }: RecommendedProductsQuery = await graphQLClient.request({
    document: RecommendedProductsDocument,
    variables: { id, lang: lang?.toUpperCase() },
  })

  const handles = {
    [lang]: product.handle,
    [alternateLang]: await getProductAlternateHandle(product.id, alternateLang),
  }

  const [mainVariant] = product.variants.nodes
  const post = await fetchProductPost(mainVariant.sku || '', getLocaleFromLanguage(lang))
  const reviews = await fetchProductReviews({ skus: productSkus as string[], includeRating: true })

  return { props: { product, post, reviews, recommendedProducts: productRecommendations, handles } }
}

const getStaticPaths: GetStaticPaths = async () => {
  const paths: string[] = []
  const langs = config.locales.map((locale) => getLanguageFromLocale(locale as Locale))

  await Promise.all(
    langs.map(async (lang) => {
      const productSlugs = (
        await fetchProductSlugs({
          lang: lang.toUpperCase(),
          first: 250,
          productType: 'gear',
        })
      ).filter(({ metafields }) => !getMetafield(metafields, 'b2b', 'exclusive'))

      if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
        // eslint-disable-next-line no-console
        console.log(`\n⚙️  will create ${productSlugs.length} pages for ${lang} gear products`)
      }

      productSlugs.forEach(({ handle }) => {
        paths.push(`/${lang}/p/${handle}`)
      })
    }),
  )

  return {
    paths,
    fallback: false,
  }
}

export { getStaticPaths, getStaticProps }

export default ProductDetailPage
